// src/pages/FAQ.js
import React from 'react';
import Layout from "../central/Layout";
import Panel from "../central/Panel";
import { HeadingSmall, BodyText } from "../central/Typography";
import Accordion from "../central/Accordion";
import theme from '../central/theme';

const tblContainer = {
  borderRadius: 10,
  overflow: "hidden",
  border: "1px solid #e5e7eb",
  marginTop: theme.spacing.small,
  marginBottom: theme.spacing.medium,
};
const tblHead = {
  background: "#50B4C8",
  color: "#fff",
  fontWeight: 700,
  padding: "9px 12px",
  textAlign: "center",
  fontSize: "0.85rem",
};
const tblHeadLeft = { ...tblHead, textAlign: "left" };
const tblCell = (rowIdx, center = true) => ({
  padding: "8px 12px",
  textAlign: center ? "center" : "left",
  background: rowIdx % 2 === 0 ? "#fff" : "#f7f9fa",
  fontSize: "0.88rem",
  borderTop: "1px solid #f0f0f0",
  verticalAlign: "middle",
});

const Li = ({ children }) => (
  <div style={{ display: "flex", gap: 10, marginBottom: 8, fontSize: "0.9rem", lineHeight: "1.45" }}>
    <span style={{ color: "#50B4C8", fontWeight: 700, flexShrink: 0, marginTop: 1 }}>›</span>
    <span>{children}</span>
  </div>
);

// Shared chip badge width — sized to Triple Captain (the longest)
const CHIP_BADGE_WIDTH = 110;
// Shared GW badge width — sized to "Join in GW3+" (the longest)
const GW_BADGE_WIDTH = 100;

const FAQ = () => {
  return (
    <Layout>
      <Panel style={{ backgroundColor: theme.colors.surface, padding: theme.spacing.medium, width: "100%", maxWidth: "720px" }}>
        <HeadingSmall style={{ marginBottom: theme.spacing.medium, fontSize: "1.4rem", fontWeight: 700 }}>
          FAQ
        </HeadingSmall>

        {/* 1. Emojis removed from all titles */}
        <Accordion title="Overview" defaultOpen={false}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingBottom: 4 }}>
            <Li><b>ClubWeek</b> is a <b>private fantasy football game for individual real-life teams.</b></Li>
            <Li><b>How it works:</b> Pick players from your team's squad, earn points based on their real match performance each gameweek, and compete on the leaderboard.</Li>
          </div>
        </Accordion>

        <Accordion title="Weekly Gameplay Basics" defaultOpen={false}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingBottom: 4 }}>
            <Li><b>Deadline:</b> Squad selection locks at the gameweek deadline. Changes after that point are not allowed.</Li>
            <Li><b>Roster Limits:</b> Each league type has specific positional rules and squad sizes. See <b>League Setup & Formats</b> below.</Li>
            <Li><b>Captains:</b> Assign a Captain each week — they score double points.</Li>
            <Li><b>Chips:</b> Special chips can be enabled by your admin. See <b>Scoring System</b> for details.</Li>
            <Li><b>Gameweek rollover:</b> Your selections carry over week to week, including your captain pick.</Li>
            <Li><b>Transfers:</b> You receive one free transfer per gameweek. Unused transfers carry over up to a max of 2–3 (depending on league type). Extra transfers cost 5 penalty points each.</Li>
            <Li><b>Budget:</b> Your squad must stay within budget. Starting budgets vary by league type.</Li>
            <Li><b>Buying &amp; Selling:</b> If you sell a player at a higher price than you bought them, the difference is added to your budget — and vice versa.</Li>
          </div>
        </Accordion>

        <Accordion title="Scoring System" defaultOpen={false}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingBottom: 8 }}>
            <Li>The admin submits match results each gameweek — takes about 1–2 minutes.</Li>
            <Li>The admin awards bonus points to the top 3 rated players. Keep them honest!</Li>
            <Li>Clean sheets provide bonus points, varying by position.</Li>
            <Li>Players who don't appear receive 0 points and cannot be swapped mid-gameweek.</Li>
          </div>

          {/* 2. Scoring table — multi-line Action cells, split DEF clean sheet value */}
          <div style={tblContainer}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={tblHeadLeft}>Action</th>
                  <th style={tblHead}>DEF</th>
                  <th style={tblHead}>MID</th>
                  <th style={tblHead}>FWD</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { action: "Appearance",       def: "+2",  mid: "+2",       fwd: "+2" },
                  { action: "Starter",           def: "+1",  mid: "+1",       fwd: "+1" },
                  { action: "Goal Scored",       def: "+8",  mid: "+7",       fwd: "+7" },
                  { action: "Assist",            def: "+5",  mid: "+3",       fwd: "+2" },
                  {
                    action: "Clean Sheet",
                    // DEF split onto two lines
                    defNode: <><span>+5</span><br /><span style={{ fontSize: "0.8rem", color: "#666" }}>(GK +2)</span></>,
                    mid: "+3",
                    fwd: "+2",
                  },
                  {
                    action: <><span>Penalty Save</span><br /><span style={{ fontSize: "0.8rem", color: "#666" }}>(GK only)</span></>,
                    def: "+5", mid: "—", fwd: "—",
                  },
                  { action: "Penalty Miss",      def: "−2",  mid: "−2",       fwd: "−2" },
                  { action: "Own Goal",          def: "−3",  mid: "−3",       fwd: "−3" },
                  { action: "Yellow Card",       def: "−4",  mid: "−4",       fwd: "−4" },
                  {
                    action: <><span>Bonus Points</span><br /><span style={{ fontSize: "0.8rem", color: "#666" }}>(1st / 2nd / 3rd)</span></>,
                    def: "+3 / 2 / 1", mid: "+3 / 2 / 1", fwd: "+3 / 2 / 1",
                  },
                ].map(({ action, def, defNode, mid, fwd }, i) => (
                  <tr key={i}>
                    <td style={tblCell(i, false)}>{action}</td>
                    <td style={tblCell(i)}>{defNode ?? def}</td>
                    <td style={tblCell(i)}>{mid}</td>
                    <td style={tblCell(i)}>{fwd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <BodyText style={{ marginBottom: 8, fontWeight: 700 }}>Chips</BodyText>
          {/* 3. Chip badges all same fixed width */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
            {[
              { name: "Triple Captain", desc: "Captain scores triple points instead of double." },
              { name: "Goal Boost",     desc: "Goal and assist points are doubled." },
              { name: "Fortress",       desc: "Clean sheet points are doubled." },
            ].map(({ name, desc }) => (
              <div key={name} style={{ background: "#f4f7f8", borderRadius: 8, padding: "10px 12px", display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{
                  background: "#50B4C8", color: "#fff", borderRadius: 6,
                  padding: "4px 8px", fontSize: "0.78rem", fontWeight: 700,
                  width: CHIP_BADGE_WIDTH, display: "inline-block",
                  textAlign: "center", flexShrink: 0,
                }}>{name}</span>
                <span style={{ fontSize: "0.88rem", color: "#444" }}>{desc}</span>
              </div>
            ))}
            <BodyText style={{ marginBottom: 0, color: "#888", fontSize: "0.82rem" }}>
              Note: captain multiplier still applies on top of Goal Boost and Fortress.
            </BodyText>
          </div>
        </Accordion>

        <Accordion title="League Setup & Formats" defaultOpen={false}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingBottom: 8 }}>
            <Li>Leagues are private — created and managed by an admin.</Li>
            <Li>Choose from 5-a-side, 7-a-side, 9-a-side, or 11-a-side. This sets squad size, budget, and positional rules.</Li>
            <Li>Pick Team enforces budget and squad composition rules automatically.</Li>
          </div>

          {/* 4. League Type column — fixed width so names never wrap */}
          <div style={tblContainer}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...tblHeadLeft, width: 90, whiteSpace: "nowrap" }}>League Type</th>
                  <th style={tblHead}>Players</th>
                  <th style={tblHead}>Budget</th>
                  <th style={tblHead}>FT</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["5-a-side",  "3", "$18.0m", "2", "−5 pts"],
                  ["7-a-side",  "4", "$24.0m", "2", "−5 pts"],
                  ["9-a-side",  "5", "$29.0m", "3", "−5 pts"],
                  ["11-a-side", "5", "$30.0m", "3", "−5 pts"],
                ].map(([type, players, budget, ft], i) => (
                  <tr key={i}>
                    <td style={{ ...tblCell(i, false), fontWeight: 600, whiteSpace: "nowrap" }}>{type}</td>
                    <td style={tblCell(i)}>{players}</td>
                    <td style={tblCell(i)}>{budget}</td>
                    <td style={tblCell(i)}>{ft}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 5. Squad Rules — two-line cell: rule on line 1, "X total players" on line 2 */}
          <BodyText style={{ marginBottom: 8, fontWeight: 700 }}>Squad Rules by League Type</BodyText>
          <div style={tblContainer}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...tblHeadLeft, width: 90, whiteSpace: "nowrap" }}>League Type</th>
                  <th style={tblHeadLeft}>Squad Rules</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["5-a-side",  "Min 0, max 2 per position", "3 total players"],
                  ["7-a-side",  "Min 1, max 2 per position", "4 total players"],
                  ["9-a-side",  "Min 1, max 3 per position", "5 total players"],
                  ["11-a-side", "Min 1, max 3 per position", "5 total players"],
                ].map(([type, rule, total], i) => (
                  <tr key={i}>
                    <td style={{ ...tblCell(i, false), fontWeight: 600, whiteSpace: "nowrap" }}>{type}</td>
                    <td style={tblCell(i, false)}>
                      <span>{rule}</span>
                      <br />
                      <span style={{ fontSize: "0.82rem", color: "#666" }}>{total}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Accordion>

        <Accordion title="Player Pricing" defaultOpen={false}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingBottom: 8 }}>
            <Li>Each player has a starting price based on their admin-assigned rating (1–5).</Li>
            <Li>Prices change weekly based on performance — the system self-corrects over time.</Li>
            <Li>Admins set ratings in good faith. Over/underpriced players will naturally drift toward fair value.</Li>
          </div>

          <div style={tblContainer}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={tblHead}>Rating</th>
                  <th style={tblHead}>DEF</th>
                  <th style={tblHead}>MID</th>
                  <th style={tblHead}>FWD</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["5", "$6.0m", "$6.5m", "$7.0m"],
                  ["4", "$5.5m", "$6.0m", "$6.5m"],
                  ["3", "$5.0m", "$5.5m", "$6.0m"],
                  ["2", "$4.5m", "$5.0m", "$5.5m"],
                  ["1", "$4.0m", "$4.5m", "$5.0m"],
                ].map(([rating, def, mid, fwd], i) => (
                  <tr key={i}>
                    <td style={{ ...tblCell(i), fontWeight: 600 }}>{rating}</td>
                    <td style={tblCell(i)}>{def}</td>
                    <td style={tblCell(i)}>{mid}</td>
                    <td style={tblCell(i)}>{fwd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Li>Prices can change weekly based on performance and demand.</Li>
            <Li>Admins can add players or change positions mid-season without affecting current prices.</Li>
          </div>
        </Accordion>

        <Accordion title="Admin Responsibilities" defaultOpen={false}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingBottom: 4 }}>
            <Li>Create the league: set name, format, players with positions and ratings, enable chips, and set the initial deadline.</Li>
            <Li>Share your league code with teammates so they can join (shown on the Manage page).</Li>
            <Li>Submit match results each gameweek. Assign bonus points to the top 3 players in good faith.</Li>
            <Li>Adjust the gameweek deadline if the match day changes, or add a week for a bye.</Li>
            <Li>Add a second match to the same gameweek for double gameweeks.</Li>
            <Li>Add new players mid-season with a position and rating.</Li>
            <Li>At season end, close the season and start a new one — your player set carries over.</Li>
          </div>
        </Accordion>

        <Accordion title="Special Situations" defaultOpen={false}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingBottom: 4 }}>
            <Li><b>Double Gameweeks:</b> The admin records a second match before advancing the gameweek. Points from both matches count.</Li>
            <Li>
              <div>
                <b>Late joiners:</b> You can join after GW1 but may face transfer penalties and a points deficit.
                {/* 6. GW panels — equal height, vertically centred, uniform badge width */}
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    ["Join in GW1",  "Unlimited free transfers — no penalty."],
                    ["Join in GW2",  "5 penalty points when picking your first team."],
                    ["Join in GW3+", "No transfer penalty, but you start behind on points already scored."],
                  ].map(([gw, desc]) => (
                    <div key={gw} style={{
                      display: "flex", gap: 10, alignItems: "center",
                      minHeight: 44,
                      background: "#f4f7f8", borderRadius: 8, padding: "10px 12px",
                    }}>
                      <span style={{
                        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6,
                        padding: "4px 8px", fontSize: "0.78rem", fontWeight: 700,
                        width: GW_BADGE_WIDTH, display: "inline-block",
                        textAlign: "center", flexShrink: 0,
                        whiteSpace: "nowrap",
                      }}>{gw}</span>
                      <span style={{ fontSize: "0.87rem", color: "#444" }}>{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Li>
          </div>
        </Accordion>

      </Panel>
    </Layout>
  );
};

export default FAQ;
